"use client";

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const DEVICES = [
    { icon: "monitor-bold", key: "Nas_cloud_dev_pc" },
    { icon: "laptop-bold", key: "Nas_cloud_dev_mac" },
    { icon: "smartphone-bold", key: "Nas_cloud_dev_phone" },
    { icon: "videocamera-record-bold", key: "Nas_cloud_dev_cam" },
];

export default function NasCloud() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [phase, setPhase] = useState(0); // 0 idle, 1 backup, 2 protégé, 3 accès, 4 fini
    const [done, setDone] = useState(0); // appareils sauvegardés (0..4)
    const timers = useRef([]);

    function clearTimers() {
        timers.current.forEach((id) => clearTimeout(id));
        timers.current = [];
    }
    useEffect(() => clearTimers, []);

    function play() {
        if (phase !== 0 && phase !== 4) return;
        clearTimers();
        setDone(0);
        setPhase(1);
        const s = (ms) => (reduce ? ms * 0.3 : ms);
        const at = (ms, fn) => timers.current.push(setTimeout(fn, s(ms)));
        at(350, () => setDone(1));
        at(700, () => setDone(2));
        at(1050, () => setDone(3));
        at(1400, () => setDone(4));
        at(1750, () => setPhase(2));
        at(2450, () => setPhase(3));
        at(3150, () => setPhase(4));
    }

    function replay() {
        clearTimers();
        setPhase(0);
        setDone(0);
        timers.current.push(setTimeout(play, 60));
    }

    const backing = phase === 1;
    const protectedNas = phase >= 2;
    const access = phase >= 3;
    const capacity = phase >= 2 ? 100 : (done / DEVICES.length) * 100;

    const statusKey =
        phase === 0
            ? "Nas_cloud_status_idle"
            : phase === 1
              ? "Nas_cloud_status_backup"
              : "Nas_cloud_status_protected";

    return (
        <section id="nas-cloud" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Nas_cloud_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Nas_cloud_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-teal-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <SolarIcon
                            name="server-bold-duotone"
                            className="w-3.5 h-3.5 text-teal-300"
                        />
                        {t("Nas_cloud_nas_name")}
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Nas_cloud_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-8">
                    <div className="max-w-md mx-auto">
                        {/* Appareils */}
                        <div className="text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 mb-3">
                            {t("Nas_cloud_devices_label")}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                            {DEVICES.map((d, i) => {
                                const ok = done > i;
                                return (
                                    <div
                                        key={d.key}
                                        className={`relative rounded-xl bg-white/[0.04] ring-1 px-2 py-3 flex flex-col items-center gap-1.5 transition-colors duration-300 ${
                                            ok ? "ring-teal-400/40" : "ring-white/10"
                                        }`}
                                    >
                                        <SolarIcon
                                            name={d.icon}
                                            className={`w-6 h-6 ${ok ? "text-teal-300" : "text-gray-400"}`}
                                        />
                                        <span className="text-[11px] text-gray-300 text-center leading-tight">
                                            {t(d.key)}
                                        </span>
                                        {ok && (
                                            <motion.span
                                                initial={reduce ? false : { scale: 0 }}
                                                animate={{ scale: 1 }}
                                                transition={{ type: "spring", stiffness: 500, damping: 18 }}
                                                className="absolute -top-1.5 -right-1.5"
                                            >
                                                <SolarIcon
                                                    name="check-circle-bold"
                                                    className="w-4 h-4 text-emerald-400"
                                                />
                                            </motion.span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Flux montant (sauvegarde) */}
                        <div className="flex justify-center py-2">
                            <div className="flex flex-col items-center -space-y-1">
                                {[0, 1, 2].map((i) => (
                                    <motion.span
                                        key={i}
                                        animate={
                                            backing && !reduce
                                                ? { opacity: [0.2, 1, 0.2] }
                                                : { opacity: 0.25 }
                                        }
                                        transition={
                                            backing && !reduce
                                                ? {
                                                      duration: 0.9,
                                                      repeat: Infinity,
                                                      delay: i * 0.12,
                                                  }
                                                : { duration: 0.2 }
                                        }
                                    >
                                        <SolarIcon
                                            name="alt-arrow-down-bold"
                                            className="w-4 h-4 text-teal-400"
                                        />
                                    </motion.span>
                                ))}
                            </div>
                        </div>

                        {/* NAS central */}
                        <div
                            className={`rounded-2xl p-5 ring-1 transition-all duration-500 ${
                                protectedNas
                                    ? "bg-teal-500/10 ring-teal-400/40"
                                    : "bg-white/[0.04] ring-white/10"
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-teal-500/15 ring-1 ring-teal-400/30 shrink-0">
                                    <SolarIcon
                                        name="server-bold-duotone"
                                        className="w-6 h-6 text-teal-300"
                                    />
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold text-white">
                                            {t("Nas_cloud_nas_name")}
                                        </span>
                                        {protectedNas && (
                                            <motion.span
                                                initial={reduce ? false : { scale: 0, opacity: 0 }}
                                                animate={{ scale: 1, opacity: 1 }}
                                                transition={{ type: "spring", stiffness: 400, damping: 18 }}
                                                className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/40 px-2 py-0.5 text-[10px] font-bold text-emerald-300"
                                            >
                                                <SolarIcon
                                                    name="shield-check-bold-duotone"
                                                    className="w-3 h-3"
                                                />
                                                {t("Nas_cloud_protected")}
                                            </motion.span>
                                        )}
                                    </div>
                                    <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
                                        <motion.div
                                            className="h-full rounded-full bg-gradient-to-r from-teal-400 to-emerald-400"
                                            initial={false}
                                            animate={{ width: `${capacity}%` }}
                                            transition={{ duration: reduce ? 0 : 0.4 }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Flux descendant (accès) */}
                        <div className="flex justify-center py-2">
                            <div className="flex flex-col items-center -space-y-1">
                                {[0, 1, 2].map((i) => (
                                    <motion.span
                                        key={i}
                                        animate={
                                            access && phase === 3 && !reduce
                                                ? { opacity: [0.2, 1, 0.2] }
                                                : { opacity: access ? 0.4 : 0.12 }
                                        }
                                        transition={
                                            access && phase === 3 && !reduce
                                                ? {
                                                      duration: 0.9,
                                                      repeat: Infinity,
                                                      delay: i * 0.12,
                                                  }
                                                : { duration: 0.2 }
                                        }
                                    >
                                        <SolarIcon
                                            name="alt-arrow-down-bold"
                                            className="w-4 h-4 text-emerald-400"
                                        />
                                    </motion.span>
                                ))}
                            </div>
                        </div>

                        {/* Vous, partout */}
                        <div
                            className={`rounded-2xl px-4 py-3 ring-1 flex items-center gap-3 transition-all duration-500 ${
                                access
                                    ? "bg-emerald-500/10 ring-emerald-400/40"
                                    : "bg-white/[0.03] ring-white/10 opacity-60"
                            }`}
                        >
                            <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 shrink-0">
                                <SolarIcon
                                    name="planet-bold"
                                    className={`w-5 h-5 ${access ? "text-emerald-300" : "text-gray-400"}`}
                                />
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className={`text-sm font-semibold ${access ? "text-white" : "text-gray-400"}`}>
                                    {t("Nas_cloud_you_label")}
                                </div>
                                {access && (
                                    <div className="text-[12px] text-emerald-300">
                                        {t("Nas_cloud_you_access")}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Statut + action */}
                        <div className="mt-6 flex flex-col items-center text-center">
                            <p className="text-sm font-medium text-gray-200 mb-3 h-5">
                                {t(statusKey)}
                            </p>
                            {phase === 0 && (
                                <button
                                    type="button"
                                    onClick={play}
                                    className="inline-flex items-center gap-2 rounded-xl bg-teal-500 hover:bg-teal-400 px-5 py-2.5 text-sm font-semibold text-bgDark-900 shadow-lg shadow-teal-500/25 transition active:scale-95"
                                >
                                    <SolarIcon name="shield-check-bold-duotone" className="w-4 h-4" />
                                    {t("Nas_cloud_cta")}
                                </button>
                            )}
                            {phase === 4 && (
                                <button
                                    type="button"
                                    onClick={replay}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.1] ring-1 ring-white/10 px-3.5 py-1.5 text-[13px] text-gray-300 transition active:scale-95"
                                >
                                    <SolarIcon name="restart-bold-duotone" className="w-3.5 h-3.5" />
                                    {t("Nas_cloud_replay")}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Révélation */}
                    <AnimatePresence>
                        {phase === 4 && (
                            <motion.div
                                initial={reduce ? false : { opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.4 }}
                                className="max-w-md mx-auto mt-6 flex items-start gap-3 rounded-2xl bg-teal-500/10 ring-1 ring-teal-400/30 p-4"
                            >
                                <SolarIcon
                                    name="shield-keyhole-bold-duotone"
                                    className="w-6 h-6 text-teal-300 shrink-0 mt-0.5"
                                />
                                <p className="text-sm text-gray-200 leading-relaxed mb-0">
                                    {t("Nas_cloud_reveal")}
                                </p>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Nas_cloud_caption")}
            </p>
        </section>
    );
}
